/**
 * Frontend types for the Avalara company sync.
 *
 * The wire format is UpperCamelCase and is mapped to these camelCase types in
 * avalaraManagementService. The sync result comes from the status endpoint
 * (GET .../management/company/sync); the POST returns 202 with no body.
 *
 * A failed nexus refresh for one company does not fail the run: it is reported
 * in nexusCompaniesFailed / nexusFailures on a COMPLETED status.
 */

export interface AvalaraNexusFailure {
  companyId: number
  /** Empty when the backend omits it; the toast then falls back to companyId. */
  companyCode: string
  error: string
}

export interface AvalaraCompanySyncResult {
  added: number
  updated: number
  deleted: number
  nexusCompaniesSucceeded: number
  /** > 0 indicates partial success on a 200 response */
  nexusCompaniesFailed: number
  nexusFailures: AvalaraNexusFailure[]
  /** ISO-8601 sync completion timestamp */
  syncedAt: string
}

export type AvalaraCompanySyncRunStatus = 'RUNNING' | 'COMPLETED' | 'FAILED'
export type AvalaraCompanySyncSource = 'ADHOC' | 'SCHEDULED'

export interface AvalaraCompanySyncError {
  code?: string
  message?: string
}

export interface AvalaraCompanySyncStatus {
  /** null: the answering pod had no run, or sent a status this UI does not know */
  status: AvalaraCompanySyncRunStatus | null
  source?: AvalaraCompanySyncSource
  requestedBy?: string
  startedAt?: string
  completedAt?: string
  result?: AvalaraCompanySyncResult
  error?: AvalaraCompanySyncError
}

export interface AvalaraCompanySyncBusyDetails {
  requestedBy?: string
  startedAt: string
  source?: AvalaraCompanySyncSource
}

export const NO_COMPANY_SYNC_STATUS: AvalaraCompanySyncStatus = { status: null }

export const COMPANY_SYNC_ERROR_CODES = {
  BUSY: 'COMPANY_SYNC_BUSY',
  NOT_STARTED: 'COMPANY_SYNC_NOT_STARTED',
  BULK_ALIGN_BUSY: 'BULK_ALIGN_BUSY',
  SAFETY_GUARD: 'SYNC_ABORTED_SAFETY_GUARD',
} as const
